import { createColumnHelper } from '@tanstack/react-table';
import { SOURCE_TAB } from '@va/constants';
import {
  CountryLocationIcon,
  PageVisitsInSessionCell,
  ReferrerCell,
  VideoRecordingButton,
} from '@va/dashboard/components';
import { isTabletDevice } from '@va/dashboard/selectors/ui';
import { useTranslate } from '@va/localization';
import { HeaderCellV8, TextCellV8 } from '@va/ui/components/data-table';
import { ListCardItemWithIcon } from '@va/ui/design-system';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { BrowserCell, DeviceCell, IpCell, OperatingSystemCell } from '../cells';
import { VisitorsListItem } from '../visitors-list-card';
import { VisitorTypeCell } from './cells/StatusCell';
import { LatestVisitorsItemV2 } from './useLatestVisitors';

const columnHelper = createColumnHelper<LatestVisitorsItemV2>();

export const useLatestVisitorsColumnsV8 = () => {
  const translate = useTranslate();
  const isTablet = useSelector(isTabletDevice);

  const columns = useMemo(() => {
    if (isTablet) {
      return [
        columnHelper.accessor('sessionStartUnix', {
          cell: (info) => {
            return <VisitorsListItem visitSession={info.row.original} />;
          },
          header: () => <div></div>,
          meta: {
            useDefaultCellStyle: false,
          },
          enableSorting: true,
        }),
      ];
    }

    return [
      columnHelper.accessor('sessionStartUnix', {
        header: (ctx) => (
          <HeaderCellV8
            className='!justify-start pl-10'
            context={ctx}
            text={translate('tab.latestVisitors.visitorsTypeAndDate')}
          />
        ),
        cell: ({ getValue, row }) => {
          const { countryCode, sessionKey, visitorType } = row.original;
          return (
            <VisitorTypeCell
              countryCode={countryCode}
              sessionKey={sessionKey}
              visitorType={visitorType}
              unixTs={getValue()}
            />
          );
        },
        meta: {
          cellClassName: '!justify-start',
        },
        minSize: 230,
        enableSorting: true,
      }),
      columnHelper.accessor('countryCode', {
        header: (ctx) => (
          <HeaderCellV8
            className='!justify-start pl-10'
            context={ctx}
            text={translate('tab.latestVisitors.countryInfo')}
          />
        ),
        cell: (info) => {
          const { latitude, longitude, sessionKey, cityName } = info.row.original;
          const countryCode = info.getValue();
          return (
            <div className='flex items-center overflow-hidden'>
              <ListCardItemWithIcon
                icon={
                  <CountryLocationIcon
                    location={{ lat: latitude, lng: longitude }}
                    countryCode={countryCode}
                    sessionId={sessionKey}
                  />
                }
                topText={`${translate(`country.name.${countryCode}`)}${cityName ? `, ${cityName}` : ''}`}
              />
            </div>
          );
        },
        minSize: 100,
        meta: { cellClassName: '!justify-start' },
      }),
      columnHelper.accessor('ip', {
        header: (ctx) => <HeaderCellV8 context={ctx} text={translate('tab.latestVisitors.visitorIp')} />,
        cell: (info) => {
          const { companyOrgName, companyOrgType, countryCode } = info.row.original;
          const ip = info.getValue();
          return (
            <IpCell value={ip} companyName={companyOrgName} companyType={companyOrgType} countryCode={countryCode} />
          );
        },
        minSize: 120,
        meta: {
          cellClassName: '!justify-start',
        },
        enableSorting: true,
      }),
      columnHelper.accessor('deviceType', {
        minSize: 150,
        header: (ctx) => <HeaderCellV8 context={ctx} text={translate('tab.latestVisitors.deviceInfo')} />,
        cell: (info) => {
          const { deviceType, platform, browserName } = info.row.original;
          return (
            <div className='flex flex-row gap-2'>
              <DeviceCell value={deviceType} />
              <OperatingSystemCell value={platform} />
              <BrowserCell value={browserName} />
            </div>
          );
        },
      }),
      columnHelper.accessor('referrerUrl', {
        minSize: 200,
        header: (ctx) => <HeaderCellV8 context={ctx} text={translate('tab.latestVisitors.referrerUrl')} />,
        cell: ({ getValue }) => {
          return <ReferrerCell referrer={getValue()} />;
        },
        enableSorting: true,
      }),
      columnHelper.accessor('unfilteredVisitCount', {
        minSize: 100,
        header: (ctx) => <HeaderCellV8 context={ctx} text={translate('tab.latestVisitors.pageVisits')} />,
        cell: (info) => {
          const { sessionKey, unfilteredVisitCount, countryCode } = info.row.original;
          return (
            <PageVisitsInSessionCell
              sessionId={sessionKey}
              pageVisitsCount={unfilteredVisitCount}
              countryCode={countryCode}
            />
          );
        },
      }),
      columnHelper.accessor('hasRecording', {
        minSize: 80,
        enableResizing: false,
        header: (ctx) => <HeaderCellV8 context={ctx} text={translate('tab.latestVisitors.sessionRecordings')} />,
        cell: (info) => {
          const { sessionKey, sessionStartUnix, hasRecording } = info.row.original;
          return (
            <VideoRecordingButton
              sourceTab={SOURCE_TAB.OVERVIEW}
              sessionInfo={{ unixTs: sessionStartUnix, id: sessionKey, hasRecording: hasRecording }}
            />
          );
        },
      }),
      columnHelper.accessor('userAgent', {
        minSize: 150,
        header: (ctx) => <HeaderCellV8 context={ctx} text={translate('tab.latestVisitors.userAgent')} />,
        cell: ({ getValue, ...context }) => {
          return <TextCellV8 getValue={() => getValue() || translate('all.notApplicable')} {...context} />;
        },
      }),
    ];
  }, [isTablet, translate]);

  return columns;
};
